import { ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import CategoryOutlined from "@mui/icons-material/CategoryOutlined";
import BuildOutlined from "@mui/icons-material/BuildOutlined";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import AssessmentOutlined from "@mui/icons-material/AssessmentOutlined";
import EventNoteOutlined from "@mui/icons-material/EventNoteOutlined";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";

const accents = { categories: "#2563eb", maintenance: "#2563eb", suppliers: "#0d9488", purchases: "#7c3aed", inventory: "#0284c7", operations: "#0f766e", reports: "#4f46e5", preventive: "#d97706" };
type PageKind = keyof typeof accents;
const icons = { categories: <CategoryOutlined />, maintenance: <BuildOutlined />, suppliers: <LocalShippingOutlined />, purchases: <ReceiptLongOutlined />, inventory: <Inventory2Outlined />, operations: <AccountTreeOutlined />, reports: <AssessmentOutlined />, preventive: <EventNoteOutlined /> };

// Presentation only: scoped to these five pages, leaving portal-wide styles intact.
export const sparePageSx = (kind: PageKind) => (theme: Theme) => ({
  p: { xs: 1.5, md: 2.5 },
  minHeight: "calc(100vh - 96px)",
  borderRadius: 3,
  background: theme.palette.mode === "dark"
    ? "linear-gradient(145deg, #101c30, #111827)"
    : `linear-gradient(145deg, ${alpha(accents[kind], .07)}, #f5f9ff 55%, #edf4fb)`,
  "& .MuiDataGrid-root": {
    fontSize: "0.78rem",
    "& .MuiDataGrid-columnHeaders": { bgcolor: "background.paper" },
    "& .MuiDataGrid-columnHeaderTitle": { textTransform: "none", fontWeight: 700 },
    "& .MuiDataGrid-row:nth-of-type(odd)": { bgcolor: alpha(accents[kind], .025) },
    "& .MuiDataGrid-row:hover": { bgcolor: alpha(accents[kind], .07) },
    "& .MuiDataGrid-cell": { borderColor: alpha(theme.palette.text.primary, .065) },
    "& .MuiDataGrid-footerContainer": { bgcolor: alpha(accents[kind], .025) },
    "& .MuiIconButton-root": { borderRadius: "8px", bgcolor: alpha(accents[kind], .045) },
  },
  "& .MuiPaper-root": {
    backgroundImage: "none",
    backdropFilter: "none",
    boxShadow: "0 5px 18px rgba(20,45,90,.035)",
    borderColor: alpha(theme.palette.text.primary, .08),
    "&:hover": { boxShadow: "0 5px 18px rgba(20,45,90,.035)" },
  },
  "& .MuiButton-containedPrimary": {
    bgcolor: accents[kind],
    boxShadow: `0 5px 13px ${alpha(accents[kind], .20)}`,
    "&:hover": { bgcolor: accents[kind], filter: "brightness(.94)" },
  },
  "& .MuiButtonBase-root:focus-visible": { outline: `3px solid ${alpha(accents[kind], .5)}`, outlineOffset: 2 },
});

export function SparePageHeader({ title, subtitle, action, kind }: { title: string; subtitle?: string; action?: ReactNode; kind: PageKind }) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={2} mb={2.5}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ display: "grid", placeItems: "center", width: 46, height: 46, flexShrink: 0, bgcolor: "background.paper", color: accents[kind], borderRadius: 2.5, boxShadow: "0 4px 12px rgba(30,65,130,.05)" }}>{icons[kind]}</Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 0, fontSize: { xs: "1.4rem", md: "1.7rem" } }}>{title}</Typography>
          {subtitle && <Typography variant="body2" color="text.secondary" mt={.5}>{subtitle}</Typography>}
        </Box>
      </Stack>
      {action && <Box sx={{ flexShrink: 0, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>{action}</Box>}
    </Stack>
  );
}

export function SpareSummary({ items }: { items: Array<{ label: string; value: ReactNode; icon: ReactNode; color?: string }> }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", md: `repeat(${items.length},minmax(0,1fr))` }, gap: 1.5, mb: 2 }}>
      {items.map(({ label, value, icon, color = "#2563eb" }) => (
        <Stack key={label} direction="row" spacing={1.5} alignItems="center" sx={(theme) => ({ p: { xs: 1.5, md: 2 }, borderRadius: 2.5, border: "1px solid", borderColor: alpha(color, .10), background: `linear-gradient(110deg, ${theme.palette.background.paper}, ${alpha(color, .035)})`, minWidth: 0 })}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(color, .10), color, display: "grid", placeItems: "center", flexShrink: 0, "& svg": { fontSize: 22 } }}>{icon}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>{label}</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", mt: .3, overflowWrap: "anywhere", fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
          </Box>
        </Stack>
      ))}
    </Box>
  );
}
